import { IModel, IAttribute, IViolation } from '../base/annotation';
import { Metamodel } from '../base/model';
import { LinkedService } from './linkedservice';
import { AzureKeyVault } from './key-vault';
import { format } from '../../shared/utils/string.util';
import { getSqlPEResourceIDAndName } from '../../extensibility/sql.util';
import { ModelConstants } from '../model-constants';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { IPrivateEndpointCompatible } from '../../interfaces/IPrivateEndpointCompatible';
import { azureCloudTypeDef } from '../../constants/linkedservice-constants';
import { SQLAuthenticationType } from '../../interfaces/IAuthenticationType';
import { Credential } from '../credential/credential';
import { SqlVersionType, SqlEncryptType, SqlAdditionalPropertiesKeyType } from '../../constants/sql-constants';
import { ParameterConfig } from '../pipeline/parameter-config';

export const azureSqlDWAuthRefTypes = {
    CredentialRefTypeAuthentication: 'credential',
    InlineRefTypeAuthentication: 'inline'
};

@Metamodel.model({
    name: ModelConstants.synapseLinkedService,
    display: [
        format(shellStringResources.ModelName_LinkedServiceTemplate, shellStringResources.AzureSynapseAnalytics),
        format(shellStringResources.ModelName_LinkedServicesTemplate, shellStringResources.AzureSynapseAnalytics)
    ]
})
export class AzureSqlDWLinkedService extends LinkedService implements IPrivateEndpointCompatible {

    public static readonly MODEL: IModel;

    public static readonly integratedSecurity: string = 'False';
    public static readonly encrypt: string = 'True';
    public static readonly connectionTimeout: string = '30';

    public static readonly SERVER_NAME: IAttribute;
    public static readonly DATABASE_NAME: IAttribute;
    public static readonly SQLPOOL_NAME: IAttribute;
    public static readonly USER_NAME: IAttribute;
    public static readonly PASSWORD: IAttribute;
    public static readonly SERVICE_PRINCIPAL_ID: IAttribute;
    public static readonly SERVICE_PRINCIPAL_KEY: IAttribute;
    public static readonly TENANT: IAttribute;
    public static readonly ENCRYPT: IAttribute;
    public static readonly TRUST_SERVER_CERTIFICATE: IAttribute;
    public static readonly HOST_NAME_IN_CERTIFICATE: IAttribute;

    private static akvValidator: (linkedservice: LinkedService, referencedResource: AzureKeyVault) => IViolation[] | undefined;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServerName,
        association: 'SERVER_NAME',
        templatable: true
    })
    public serverName?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_DatabaseName,
        association: 'DATABASE_NAME',
        templatable: true
    })
    public databaseName?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_SqlPoolName,
        association: 'SQLPOOL_NAME',
        templatable: true
    })
    public sqlPoolName?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_UserName,
        association: 'USER_NAME',
        templatable: true
    })
    public userName?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_Password,
        constraint: {
            validator: AzureSqlDWLinkedService.validate
        },
        association: 'PASSWORD',
        templatable: true,
        accepts: ['String']
    })
    public password?: string | AzureKeyVault;

    public enableAKV?: boolean = false;
    @Metamodel.attribute({
        constraint: {
            validator: AzureSqlDWLinkedService.validate
        }
    })
    public referedAKV?: AzureKeyVault;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_AzureCloudType
    })
    public azureCloudType?: azureCloudTypeDef;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalID,
        association: 'SERVICE_PRINCIPAL_ID',
        templatable: true
    })
    public servicePrincipalId?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalKey,
        constraint: {
            validator: AzureSqlDWLinkedService.validate
        },
        association: 'SERVICE_PRINCIPAL_KEY',
        templatable: true,
        accepts: ['String']
    })
    public servicePrincipalKey?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_Tenant,
        association: 'TENANT',
        templatable: true
    })
    public tenant: string;

    @Metamodel.attribute({
        display: shellStringResources.Credential,
        reference: true,
        constraint: {
            validator: AzureSqlDWLinkedService.validateCredential
        }
    })
    public credential?: Credential;

    public authReferenceMethod?: string = azureSqlDWAuthRefTypes.InlineRefTypeAuthentication;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_AuthenticationType
    })
    public authenticationType?: SQLAuthenticationType;

    protected _serverResourceID?: string;

    public sqlVersion: SqlVersionType;

    //#region: properties in sql v2
    @Metamodel.attribute({
        display: shellStringResources.ModelName_Encrypt,
        association: 'ENCRYPT',
        templatable: true
    })
    public encrypt?: SqlEncryptType;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_TrustServerCertificate,
        association: 'TRUST_SERVER_CERTIFICATE',
        templatable: true
    })
    public trustServerCertificate?: boolean;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_HostNameInCertificate,
        association: 'HOST_NAME_IN_CERTIFICATE',
        templatable: true
    })
    public hostNameInCertificate?: string;

    public parameterizableAdditionalFields: Array<ParameterConfig<SqlAdditionalPropertiesKeyType>>;
    //#endregion

    constructor(name?: string, description?: string) {
        super(name, description);
    }

    private static validateCredential(linkedservice: AzureSqlDWLinkedService, referencedCredential: Credential): IViolation[] | undefined {
        if (linkedservice &&
            linkedservice.authenticationType === SQLAuthenticationType.Credential &&
            !referencedCredential) {
            return [{
                context: linkedservice,
                message: () => shellStringResources.Error_InvalidCredentialReferenced
            }];
        }
        return undefined;
    }

    private static validate(linkedservice: LinkedService, referencedResource: AzureKeyVault): IViolation[] | undefined {
        if (AzureSqlDWLinkedService.akvValidator) {
            return AzureSqlDWLinkedService.akvValidator(linkedservice, referencedResource);
        }
        return undefined;
    }

    public getPEResourceIDAndName(additionalInfo?: { [key: string]: string }): { id: string, name: string } {
        let groupID = 'sql';
        if (this.serverName) {
            if (this.serverName.indexOf('-ondemand.') > -1) {
                groupID = 'sqlOnDemand';
            }
            // second highest priority based on domain
            if (additionalInfo && additionalInfo['sqlDomain']) {
                // match old dedicated sql domain
                if (this.serverName.toLowerCase().endsWith(additionalInfo['sqlDomain'])) {
                    groupID = 'sqlServer';
                }
            }
        }
        const result = getSqlPEResourceIDAndName(this.serverName, this._serverResourceID, groupID);
        // highest priority based on resource provider
        if (result.id && result.id.toLowerCase().indexOf('microsoft.sql/servers') > 0) {
            groupID = 'sqlServer';
        }
        return result;
    }

    public setPEResourceID(id: string): void {
        this._serverResourceID = id;
    }
}
